<script setup>
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useDisplay } from "vuetify"
import debounce from "lodash/debounce"
import { useModalStore } from "@/stores/ModalStore"
import { useTagsStore } from "@/stores/TagsStore"
import STag from "@/components/chips/STag.vue"
import TablePaginationComplete from "@/components/pagination/TablePaginationComplete.vue"

/**
 * Tags management list (opened via modalStore.openModal('tagsManager', '', false, { type })).
 * params.type: 'events' | 'clients' - preselected tab and default availability of new tags
 */
const THIS_MODAL_NAME = 'tagsManager'

const modalStore = useModalStore()
const tagsStore = useTagsStore()
const { t } = useI18n()
const { mobile } = useDisplay()

const params = modalStore.modalParams[THIS_MODAL_NAME] ?? {}

const tabs = [
  { value: 'all', title: 'tag_tab_all' },
  { value: 'clients', title: 'tag_tab_clients' },
  { value: 'events', title: 'visits' },
]

const type = ref(params.type ?? 'all')
const search = ref('')
const MIN_SEARCH_LENGTH = 2

// Phrase sent to the backend; shorter input doesn't filter yet
const query = computed(() => {
  const text = (search.value ?? '').trim()

  return text.length >= MIN_SEARCH_LENGTH ? text : ''
})

// Sorting "column-direction", same options as client groups; newest first by default
const NEWEST = 'create_time-desc'
const sort = ref(NEWEST)
const sortOptions = [
  { title: 'newest', value: 'create_time-desc' },
  { title: 'oldest', value: 'create_time-asc' },
  { title: 'name_a_z', value: 'name-asc' },
  { title: 'name_z_a', value: 'name-desc' },
]

const page = ref(1)
const perPage = ref(5)
const tags = ref([])
const total = ref(0)
// Counts per filter chip (keys = tab values); `all` also feeds "x z y filtrowanych (z z wszystkich)"
const counts = ref({ all: 0, clients: 0, events: 0 })
const isLoading = ref(true) // true from start: no empty "no results" flash before the first load

// Skeleton takes the exact height of the list it replaces, so the dialog doesn't jump
const ROW_HEIGHT = 50 // list row with small icon buttons
const listRef = ref(null)
const skeletonHeight = ref(perPage.value * (ROW_HEIGHT + 1) - 1) // rows + 1px dividers, before first measure

let isResetting = false // filter watchers skip loading while showNewest() resets them

const loadTags = async () => {
  if (isResetting) return
  if (listRef.value) skeletonHeight.value = listRef.value.$el.offsetHeight
  isLoading.value = true
  try {
    const response = await tagsStore.listTags({
      page: page.value,
      perPage: perPage.value,
      search: query.value,
      type: type.value === 'all' ? null : type.value,
      column: sort.value.split('-')[0],
      direction: sort.value.split('-')[1],
    })

    if (response.success) {
      tags.value = response.data.tags
      total.value = response.meta.total
      counts.value = {
        all: response.meta.totalAll,
        clients: response.meta.totalClients,
        events: response.meta.totalEvents,
      }
    }
  } finally {
    isLoading.value = false
  }
}

const reloadFromFirstPage = () => {
  if (page.value === 1) loadTags()
  else page.value = 1 // page watcher reloads
}

const debouncedReload = debounce(reloadFromFirstPage, 300)

watch(page, loadTags)
watch([type, perPage, sort], reloadFromFirstPage)
// Searching looks through all tags; switching the filter reloads by itself
watch(query, value => {
  if (value && type.value !== 'all') type.value = 'all'
  else debouncedReload()
})

// New tag always on top: clear search, newest first, first page - in one reload
// (otherwise sort/page watchers load at once and the search one 300ms later, aborting each other)
const showNewest = async () => {
  isResetting = true
  search.value = ''
  sort.value = NEWEST
  page.value = 1
  await nextTick() // filter watchers run now and skip loading
  debouncedReload.cancel()
  isResetting = false
  loadTags()
}

// Any save/delete (also from the edit dialog) refreshes the list
watch(() => tagsStore.lastChange, ({ action }) => action === 'created' ? showNewest() : loadTags())

// ========================================
// Actions
// ========================================
const openEdit = tag => {
  modalStore.openModal('editTag', '', false, {
    tag,
    name: search.value,
    type: type.value === 'all' ? params.type : type.value,
  })
}

const removeTag = async tag => {
  const confirmed = await modalStore.openConfirmationModal(t('delete_tag_confirm', { name: tag.name }), t('delete_tag'))
  if (confirmed) await tagsStore.deleteTag(tag)
}

const close = () => modalStore.closeModal(THIS_MODAL_NAME)

onMounted(loadTags)
</script>

<template>
  <VDialog
    :model-value="modalStore.modals.tagsManager"
    persistent
    max-width="560"
    :fullscreen="mobile"
    @update:model-value="close"
  >
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        {{ $t('tags') }}
        <div class="d-flex align-center ga-4">
          <!-- Same as "add" button in reserve list -->
          <VBtn
            v-if="tagsStore.canCreate[params.type]"
            variant="outlined"
            color="green"
            :title="$t('add')"
            size="small"
            prepend-icon="ri-add-large-line"
            @click="openEdit(null)"
          >
            {{ $t('add') }}
          </VBtn>
          <VIcon
            icon="ri-close-line"
            @click="close"
          />
        </div>
      </VCardTitle>

      <VCardText class="d-flex flex-column ga-4 mt-0 pt-2">
        <div class="d-flex align-center ga-2">
          <VTextField
            v-model="search"
            :autofocus="!mobile"
            persistent-placeholder
            :label="$t('search_tag')"
            :placeholder="$t('type_at_least_2chars')"
            prepend-inner-icon="ri-search-line"
            density="compact"
            color="secondary"
            hide-details
            maxlength="50"
            clearable
            @click:clear="search = ''"
          />
          <!-- Same look as sorting select in client groups -->
          <VSelect
            v-model="sort"
            :items="sortOptions"
            :item-title="option => $t(option.title)"
            :label="$t('sorting')"
            density="compact"
            color="gray"
            hide-details
            style="max-width: 148px;"
          />
        </div>

        <!-- Same filter look as vouchers/tickets in customer card -->
        <VChipGroup
          v-model="type"
          class="chip-group-custom w-100 mt-n2"
          selected-class="v-chip--selected"
          variant="outlined"
          mandatory
          center-active
          scroll-to-active
          filter
          :show-arrows="mobile"
        >
          <VChip
            v-for="tab in tabs"
            :key="tab.value"
            :value="tab.value"
          >
            {{ $t(tab.title) }}
            <template #prepend>
              <VIcon
                v-if="tab.value==='clients' || tab.value==='events'"
                :icon="tab.value==='clients' ? 'ri-user-fill' : tab.value==='events' ? 'ri-calendar-fill' : undefined "
                size="14"
                class="mx-1"
              />
            </template>
            <template #append>
              <VBadge
                v-if="counts[tab.value] > 0"
                class="ml-1 chip-count-badge"
                inline
                :content="counts[tab.value]"
              />
            </template>
          </VChip>
        </VChipGroup>

        <VSkeletonLoader
          v-if="isLoading"
          :type="`list-item@${tags.length || perPage}`"
          :height="skeletonHeight"
          class="bg-transparent overflow-hidden"
        />

        <VList
          v-else
          ref="listRef"
          density="compact"
          class="py-0"
        >
          <template
            v-for="(tag, index) in tags"
            :key="tag.id"
          >
            <VDivider v-if="index" />
            <VListItem class="px-0">
              <STag
                :text="tag.name"
                :color="tag.color"
                class="cursor-pointer mr-1"
                @click="openEdit(tag)"
              />
              <template #append>
                <span
                  v-if="tag.forClients"
                  class="text-body-2 text-medium-emphasis me-3"
                  :title="$t('tag_clients_count', { count: tag.clientsCount })"
                >
                  <VIcon
                    icon="ri-user-fill"
                    size="14"
                  />
                  {{ tag.clientsCount }}
                </span>
                <span
                  v-if="tag.forEvents"
                  class="text-body-2 text-medium-emphasis me-3"
                  :title="$t('tag_events_count', { count: tag.eventsCount })"
                >
                  <VIcon
                    icon="ri-calendar-fill"
                    size="14"
                  />
                  {{ tag.eventsCount }}
                </span>
                <!-- Same order and colors as in client groups -->
                <VBtn
                  icon="ri-delete-bin-line"
                  variant="text"
                  size="small"
                  color="error"
                  :aria-label="$t('delete')"
                  @click="removeTag(tag)"
                />
                <VBtn
                  icon="ri-pencil-line"
                  variant="text"
                  size="small"
                  color="gray"
                  :aria-label="$t('edit')"
                  @click="openEdit(tag)"
                />
              </template>
            </VListItem>
          </template>
          <div
            v-if="!tags.length && !isLoading"
            class="text-center text-medium-emphasis pa-4"
          >
            {{ $t('no_results') }}
          </div>
        </VList>
      </VCardText>

      <!-- Footer separated from the body, like in reserve list; always shown so "Cancel" stays with no results -->
      <VDivider />
      <TablePaginationComplete
        v-model="page"
        v-model:per-page="perPage"
        :length="Math.ceil(total / perPage)"
        show-results-summary
        :filtered-count="total"
        :total-count="counts.all"
        :what="$t('tags_pagination')"
        space-between
        close-modal="tagsManager"
        class="py-2"
      />

      <!--      <VCardActions class="pa-4 pt-0 justify-start">-->
      <!--        <VBtn-->
      <!--          variant="outlined"-->
      <!--          color="primary"-->
      <!--          @click="close"-->
      <!--        >-->
      <!--          {{ $t('cancel') }}-->
      <!--        </VBtn>-->
      <!--      </VCardActions>-->
    </VCard>
  </VDialog>
</template>
